import axios from 'axios'
import { getItem } from '@/store/localStorage'
import { account } from '@/store/account.module'

export const logoURL = process.env.VUE_APP_MEDIA_URL
export const cvURL = process.env.VUE_APP_MEDIA_URL

// Create axios instance
const APIKit = axios.create({
  baseURL: '/api',
  timeout: 30000, // Reduced timeout to 30 seconds
  headers: {
    'Access-Control-Allow-Origin': '*',
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache',
    Pragma: 'no-cache',
    Expires: '0',
  },
})

// Request interceptor
APIKit.interceptors.request.use(
  async (config) => {
    const user = await getUser()
    const token = await getToken()

    if (user && token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // Add cancelToken to the request
    const source = axios.CancelToken.source()
    config.cancelToken = source.token

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor
APIKit.interceptors.response.use(
  (response) => {
    return response.data
  },
  async (error) => {
    if (axios.isCancel(error)) {
      // Handle cancelled request
      console.log('Request cancelled:', error.message)
      return Promise.reject(error)
    }

    if (error.response?.status === 401) {
      await logout()
    }
    return Promise.reject(error)
  }
)

async function getUser() {
  return account.state.loggeduser
    ? account.state.loggeduser
    : JSON.parse(getItem('loggeduser'))
}

async function logout() {
  const store = modelConfig.store
  if (store) {
    if (window.location.pathname !== '/') {
      await store.dispatch('account/logout', window.location.pathname)
    } else {
      await store.dispatch('account/logout')
    }
  }
}

async function getToken() {
  return account.state.token ? account.state.token : getItem('token')
}

// Add retry logic
const retryRequest = async (fn, retries = 3, delay = 1000) => {
  try {
    return await fn()
  } catch (error) {
    if (retries === 0 || axios.isCancel(error)) {
      throw error
    }
    await new Promise((resolve) => setTimeout(resolve, delay))
    return retryRequest(fn, retries - 1, delay * 2)
  }
}

// Generic request function with retry logic
async function makeRequest(method, url, payload, config = {}) {
  const requestFn = async () => {
    try {
      const response = await APIKit.request({
        method,
        url,
        data: payload,
        ...config,
      })
      return response
    } catch (error) {
      if (axios.isCancel(error)) {
        throw error
      }
      throw error
    }
  }

  return retryRequest(requestFn)
}

// HTTP method shortcuts
export async function get(url, config) {
  return await makeRequest('get', url, null, config)
}

export async function del(url, config) {
  return await makeRequest('delete', url, null, config)
}

export async function post(url, payload, config) {
  return await makeRequest('post', url, payload, config)
}

export async function put(url, payload, config) {
  return await makeRequest('put', url, payload, config)
}

export async function filePost(url, payload, config) {
  const fileConfig = {
    ...config,
    headers: {
      'Content-Type': 'multipart/form-data',
      ...(config?.headers || {}),
    },
  }
  return await makeRequest('post', url, payload, fileConfig)
}

export async function refer(user) {
  const bodyFormData = new FormData()
  const body = !user.SourceFileId ? 'Files/CreateFile' : 'Files/CopyFile'

  bodyFormData.append('FileTypeId', user.FileTypeId)
  bodyFormData.append('ReferenceId', user.ReferenceId)
  bodyFormData.append('CreateUser', user.CreateUser)
  bodyFormData.append('IsDefault', 1)
  bodyFormData.append('Description', user.Description)
  bodyFormData.append('CandidateName', user.firstname)
  bodyFormData.append('CandidateSurname', user.lastname)
  bodyFormData.append('AppliedWorkName', user.company_name)

  if (!user.SourceFileId) {
    bodyFormData.append('File', user.File)
  } else {
    bodyFormData.append('SourceFileId', user.SourceFileId)
  }

  return await filePost(body, bodyFormData)
}

export const modelConfig = { store: null }

// Export cancel token creator for components to use
export const createCancelToken = () => axios.CancelToken.source()
